import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  HelpCircle,
  MessageSquare,
  Info,
  ChevronDown,
  ExternalLink,
  LogOut,
} from 'lucide-react'
import { FeedbackForm } from './FeedbackForm'
import { MAIN_APP_URL } from '../../config'

const APP_VERSION = '1.0.0'

interface MorePanelProps {
  actorId?: string
  onLogout?: () => void
  onOpenHelp?: () => void
}

/**
 * "More" tab panel with help guides, bug report, and about section.
 */
export function MorePanel({ actorId, onLogout, onOpenHelp }: MorePanelProps) {
  const { t } = useTranslation()

  return (
    <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3">
      {/* Help — opens dedicated help page */}
      <button
        onClick={onOpenHelp}
        className="flex items-center gap-2 w-full px-3 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-left"
      >
        <HelpCircle size={14} className="text-primary-500 dark:text-primary-400 shrink-0" />
        <span className="text-[13px] font-semibold text-neutral-900 dark:text-neutral-100 flex-1">
          {t('more.helpTitle')}
        </span>
        <ChevronDown size={14} className="text-neutral-400 -rotate-90" />
      </button>

      {/* Feedback */}
      <Section icon={MessageSquare} title={t('more.feedbackTitle')}>
        <FeedbackForm actorId={actorId} />
      </Section>

      {/* About */}
      <Section icon={Info} title={t('more.aboutTitle')}>
        <AboutSection />
      </Section>

      {/* Logout — always visible */}
      {onLogout && (
        <button
          onClick={onLogout}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg border border-error-200 dark:border-error-800 text-xs font-medium text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/30 transition-colors"
        >
          <LogOut size={14} />
          {t('auth.logoutButton')}
        </button>
      )}
    </div>
  )
}

// ============================================================
// Collapsible Section
// ============================================================

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof HelpCircle
  title: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
      >
        <Icon size={14} className="text-primary-500 dark:text-primary-400 shrink-0" />
        <span className="text-[13px] font-semibold text-neutral-900 dark:text-neutral-100 flex-1">
          {title}
        </span>
        <ChevronDown
          size={14}
          className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-neutral-100 dark:border-neutral-800">
          {children}
        </div>
      )}
    </div>
  )
}

// ============================================================
// About Section
// ============================================================

function AboutSection() {
  const { t } = useTranslation()

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] text-neutral-500 dark:text-neutral-400">
        <span>{t('more.version')}</span>
        <span className="font-mono">{APP_VERSION}</span>
      </div>

      <a
        href={MAIN_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:underline"
      >
        <ExternalLink size={10} />
        {t('more.visitKobSign')}
      </a>

      <a
        href="https://docs.kobsign.com"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:underline"
      >
        <ExternalLink size={10} />
        {t('more.documentation')}
      </a>
    </div>
  )
}
